import React, { useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTextField, FormSelect, FormTagsInput } from '@personal-dashboard/ui';

interface MealLog {
  id: string;
  date: string;
  mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK';
  recipe?: {
    id: string;
    title: string;
    ingredients: string[];
  } | null;
  customName?: string | null;
  ingredients: string[];
}

interface MealFormValues {
  mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK';
  mealSource: 'RECIPE' | 'CUSTOM';
  recipeId: string;
  customName: string;
  ingredients: string[];
}

interface MealLogDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: MealFormValues) => Promise<void> | void;
  log: MealLog | null;
  recipes: any[];
}

const MealLogDialog: React.FC<MealLogDialogProps> = ({ open, onClose, onSubmit, log, recipes }) => {
  const methods = useForm<MealFormValues>({
    defaultValues: {
      mealType: 'BREAKFAST',
      mealSource: 'RECIPE',
      recipeId: '',
      customName: '',
      ingredients: [],
    },
  });

  const { watch, setValue, reset, handleSubmit } = methods;
  const mealSource = watch('mealSource');
  const selectedRecipeId = watch('recipeId');

  // Handle recipe selection to pre-populate ingredients
  useEffect(() => {
    if (mealSource === 'RECIPE' && selectedRecipeId) {
      const selectedRecipe = recipes.find((r: any) => r.id === selectedRecipeId);
      if (selectedRecipe) {
        setValue('ingredients', selectedRecipe.ingredients || []);
      }
    }
  }, [selectedRecipeId, mealSource, recipes, setValue]);

  // Reset form when dialog opens or editing log changes
  useEffect(() => {
    if (open) {
      if (log) {
        const source = log.recipe ? 'RECIPE' : 'CUSTOM';
        reset({
          mealType: log.mealType,
          mealSource: source,
          recipeId: log.recipe?.id || '',
          customName: log.customName || '',
          ingredients: log.ingredients || [],
        });
      } else {
        reset({
          mealType: 'BREAKFAST',
          mealSource: 'RECIPE',
          recipeId: recipes[0]?.id || '',
          customName: '',
          ingredients: recipes[0]?.ingredients || [],
        });
      }
    }
  }, [open, log, recipes, reset]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {log ? 'Edit Meal Log' : 'Log a Meal'}
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormSelect
              name="mealType"
              label="Meal Type"
              required
              options={[
                { value: 'BREAKFAST', label: 'Breakfast' },
                { value: 'LUNCH', label: 'Lunch' },
                { value: 'DINNER', label: 'Dinner' },
                { value: 'SNACK', label: 'Snack' },
              ]}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Meal Source
              </Typography>
              <RadioGroup
                row
                value={mealSource}
                onChange={(e) => setValue('mealSource', e.target.value as 'RECIPE' | 'CUSTOM')}
              >
                <FormControlLabel value="RECIPE" control={<Radio />} label="Link to Recipe" />
                <FormControlLabel value="CUSTOM" control={<Radio />} label="Custom Meal" />
              </RadioGroup>
            </Box>

            {mealSource === 'RECIPE' ? (
              recipes.length === 0 ? (
                <Typography variant="body2" color="error">
                  You don't have any saved recipes. Please switch to "Custom Meal" or create recipes first.
                </Typography>
              ) : (
                <FormSelect
                  name="recipeId"
                  label="Choose Recipe"
                  required
                  options={recipes.map((r: any) => ({ value: r.id, label: r.title }))}
                />
              )
            ) : (
              <FormTextField
                name="customName"
                label="Meal Name"
                fullWidth
                required
              />
            )}

            <FormTagsInput
              name="ingredients"
              label="Ingredients"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={mealSource === 'RECIPE' && recipes.length === 0}
            >
              Save Log
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default MealLogDialog;
export type { MealFormValues, MealLog };
